import cx from "classnames";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from 'yup';
import Checkbox from "../../common/components/Forms/Checkbox/Checkbox";
import Icon from "../../common/components/Icon/Icon";
import { Gender } from "../../common/interfaces/enums/Gender";
import EnumMapper from "../../compare/api/mapper/EnumMapper";
import AFLayout from "../components/AFLayout";
import AFNavigation from "../components/AFNavigation";
import { TitleOptions } from "../constants/SetupData";
import { PageType } from "../enums/PageType";
import { useAFForm } from "../hooks/useAFForm";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import { IViewContext, ViewContext } from "../providers/ViewStateProvider";

type AFParams = {
    applicant: string;
};

type PersonalDetailsForm = {
    title: string;
    firstName: string;
    middleName: string;
    lastName: string;
    gender: Gender;
    isSmoker: boolean,
    dateOfBirth: string,
    isPolicyOwner: boolean
};


const AFPersonalDetailsPage: React.FC = () => {
    const applicantRef = useParams<AFParams>().applicant;
    const { actions, selectors } = useContext(AppFormControllerContext);
    const { setError, isLoading } = useContext<IViewContext>(ViewContext);

    // Ensure we have the applicant info available.
    const applicantInfo = selectors.getCurrentApplicant(applicantRef);
    const displayPolicyOwner = !applicantInfo.isChild && selectors.getApplicantList().filter(a => !a.isChild).length > 1

    const {
        getCurrentPage,
        loadFormPage,
        updateApplicant,
        navigateForward,
        navigateBackward,
        handleFieldChange,
        saveFormChanges
    } = useAFForm({ pageType: PageType.Personal, currentClientNumber: applicantInfo?.clientNumber ?? 1 });


    const [formModel, setFormModel] = useState<PersonalDetailsForm>({  // Default data that will be overridden when loading from the server.
        title: applicantInfo?.title ?? '',
        firstName: applicantInfo?.firstName ?? '',
        middleName: applicantInfo?.middleName ?? '',
        lastName: applicantInfo?.lastName ?? '',
        gender: EnumMapper.Gender.remoteToLocal(applicantInfo?.gender),
        isSmoker: applicantInfo?.isSmoker,
        dateOfBirth: applicantInfo?.dateOfBirth ?? '',
        isPolicyOwner: applicantInfo?.isPolicyOwner
    });


    // Form navigation

    const handleSubmit = useCallback((values: PersonalDetailsForm, { setSubmitting }: FormikHelpers<PersonalDetailsForm>): void => {
        saveFormChanges(() => {     // Saved successfully
            const updatedApplicant = {
                ...applicantInfo,
                title: values.title,
                firstName: values.firstName,
                middleName: values.middleName,
                lastName: values.lastName,
                gender: EnumMapper.Gender.localToRemote(values.gender),
                isSmoker: values.isSmoker,
                dateOfBirth: values.dateOfBirth.toString(),
                isPolicyOwner: values.isPolicyOwner
            };

            updateApplicant(updatedApplicant);
            setSubmitting(false);
            navigateForward(updatedApplicant);
        },
            () => {     // Bypassed - error or nothing to save
                setSubmitting(false);
            });

    }, [applicantInfo, saveFormChanges, navigateForward]);

    const handleBack = useCallback((e): void => {
        e.preventDefault();
        if (isLoading()) return;

        updateApplicant(applicantInfo);
        navigateBackward(applicantInfo);
    }, [applicantInfo, navigateBackward]);


    // Initial load (when the current applicant changes)

    const updateLoadedFields = useCallback((responses: any): void => {
        setFormModel({ ...formModel, ...responses, isPolicyOwner: responses.isPolicyOwner === "true" || responses.isPolicyOwner === "1" });
    }, []);

    const resetForm = useCallback((): void => {
        setFormModel({
            title: '',
            firstName: '',
            middleName: '',
            lastName: '',
            gender: Gender.Unknown,
            isSmoker: undefined,
            dateOfBirth: '',
            isPolicyOwner: false
        });
    }, []);


    useEffect(() => {
        loadFormPage(updateLoadedFields, resetForm);
    }, [applicantRef, loadFormPage]);


    //validation

    const validationSchema = Yup.object({
        title: Yup.string().required('Title is required'),
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required'),
        gender: Yup.string()
            .oneOf(['1', '2'], 'Please select a valid gender option'),
        isSmoker: Yup.string()
            .oneOf(['1', '0'], 'Please select a valid smoker option'),
        dateOfBirth: Yup.date()
            .required('Date of birth is required')
            .max(new Date(), 'Date of birth cannot be in the future')
            .test('is-not-today', 'Date of birth cannot be today', (value) => {
                const today = new Date();
                return value && value.toDateString() !== today.toDateString();
            })
    });


    // Other component events

    const showBirthdateReasoning = () => {
        setError("This hasn't been implemented yet.");
    };

    /*abTesting.convertAll(20, "", getProductTypeAsString, 0);
    abTesting.convertAll(18, "", getProductTypeAsString, 0);*/
    return (
        <AFLayout currentPage={getCurrentPage()}>
            <Formik
                initialValues={formModel}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={handleSubmit}
                validateOnBlur={true}
                validateOnChange={true}
                validateOnMount={true}>
                {({ errors, touched, isValid, isSubmitting, handleBlur, values, setFieldValue }) => {
                    const onBlur = (e) => {
                        handleBlur(e);
                        saveFormChanges(null, null);
                    };
                    const handlePolicyOwnerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const { checked } = e.target;
                        setFieldValue('isPolicyOwner', checked);
                        saveFormChanges(null, null);
                    };
                    return (
                        <Form noValidate onChange={handleFieldChange}>
                            <div className={cx("panel-std-white", "layout-panel-content", "gap-post-block")}>
                                <h1>Personal details</h1>
                                <h3>{applicantInfo.firstName ? applicantInfo.firstName : "Applicant" + applicantInfo.clientNumber}</h3>
                                <p>
                                    Great job choosing {selectors.getInsurerName()} for your insurance needs!
                                    Let's get started with some basic information about yourself and then
                                    get into some medical history so we can get you covered as soon as possible.
                                </p>

                                <div className="form-field">
                                    <label className={cx("label-compound")}>Name:</label>
                                    <Field id="title" name="title" as="select" onBlur={onBlur}
                                        className={cx(
                                            {
                                                "error": touched.title && errors.title,
                                                "gap-m": !(touched.title && errors.title),
                                                "placeholder-text": values.title === ""
                                            })} >
                                        <option value="" key={"option-0"} hidden> Title * </option>
                                        {TitleOptions.map((option, i) => (
                                            <option value={option.value} key={`option-${i + 1}`} className="option-black">
                                                {option.label}
                                            </option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="title" component="div" className="text-error gap-m" />
                                    <Field id="firstName" name="firstName" placeholder="First name *" onBlur={onBlur}
                                        className={cx(
                                            {
                                                "error": touched.firstName && errors.firstName,
                                                "gap-m": !(touched.firstName && errors.firstName)
                                            })} />
                                    <ErrorMessage name="firstName" component="div" className="text-error gap-m" />
                                    <Field id="middleName" name="middleName" placeholder="Middle name" onBlur={onBlur} className="gap-m" />
                                    <Field id="lastName" name="lastName" placeholder="Last name *" onBlur={onBlur}
                                        className={cx(
                                            {
                                                "error": touched.lastName && errors.lastName
                                            })} />
                                    <ErrorMessage name="lastName" component="div" className="text-error" />
                                </div>

                                <div className="form-field">
                                    <label>Gender</label>
                                    <div>
                                        <Field id="genderMale" name="gender" value="1" type="radio" onBlur={onBlur} className={touched.gender && errors.gender ? 'error' : ''} />
                                        <label className="btn-check" htmlFor="genderMale">Male</label>
                                        <Field id="genderFemale" name="gender" value="2" type="radio" onBlur={onBlur} className={touched.gender && errors.gender ? 'error' : ''} />
                                        <label className="btn-check" htmlFor="genderFemale">Female</label>
                                        <ErrorMessage name="gender" component="div" className="text-error" />
                                    </div>
                                </div>

                                <div className="form-field">
                                    <label>Smoking status (past 12 months)</label>
                                    <div>
                                        <Field id="isSmokerYes" name="isSmoker" value="1" type="radio" onBlur={onBlur} className={touched.isSmoker && errors.isSmoker ? 'error' : ''} />
                                        <label className="btn-check" htmlFor="isSmokerYes">Smoker</label>
                                        <Field id="isSmokerNo" name="isSmoker" value="0" type="radio" onBlur={onBlur} className={touched.isSmoker && errors.isSmoker ? 'error' : ''} />
                                        <label className="btn-check" htmlFor="isSmokerNo">Non-Smoker</label>
                                        <ErrorMessage name="isSmoker" component="div" className="text-error" />
                                    </div>
                                </div>

                                <div className="form-field">
                                    <label>What is your date of birth?</label>
                                    <Field id="dateOfBirth" name="dateOfBirth" type="date" onBlur={onBlur}
                                        className={cx(
                                            {
                                                "error": touched.dateOfBirth && errors.dateOfBirth,
                                                "placeholder-text": !values.dateOfBirth
                                            })} />
                                    <ErrorMessage name="dateOfBirth" component="div" className="text-error" />
                                    {/*<button onClick={showBirthdateReasoning} type="button" className="btn-link text-left">Why do we ask this?</button>*/}
                                </div>
                                <p>
                                    <strong>Please note:</strong> Altering your gender, smoking status, or age from your quote will affect your quoted premiums.
                                </p>
                                {displayPolicyOwner && (
                                    <>
                                        <h3>Policy Ownership</h3>
                                        <p>By default, you will be an owner for your own cover.
                                            <a target="_blank" href="/article/who-owns-a-life-insurance-policy">
                                                <Icon
                                                    alt={"Help"}
                                                    className="space-m space-pre-m"
                                                    size="l"
                                                    id={"Help"}
                                                />Learn more</a></p>
                                        <div className="gap-pre-2xl gap-xl">
                                            <div className={cx("layout-flex-inline gap-m")}>
                                                <Checkbox
                                                    className="space-l"
                                                    checked={values.isPolicyOwner}
                                                    name={"isPolicyOwner"}
                                                    size="2xl"
                                                    isDisabled={isSubmitting}
                                                    onChange={handlePolicyOwnerChange}>
                                                </Checkbox>
                                                <span className={cx("gap-s")}>
                                                    <strong>{applicantInfo.firstName ? applicantInfo.firstName : "Applicant" + applicantInfo.clientNumber} is an owner for all cover on this application</strong>
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <AFNavigation
                                handleBack={handleBack}
                                skipIsVisible={false}
                                backIsVisible={applicantRef !== "applicant1"}
                                forwardIsVisible={true}
                                forwardIsDisabled={!isValid || isSubmitting}
                                forwardType="submit"
                                forwardText="Next"
                            />
                        </Form>
                    );
                }}
            </Formik>
        </AFLayout>
    );
};

export default AFPersonalDetailsPage;
